/**
 * Le rotte della dashboard.
 *
 * Ogni pagina è caricata a richiesta con `React.lazy`: prima tutta
 * l'applicazione stava in un chunk solo da un megabyte, che il browser
 * scaricava per intero anche solo per vedere la pagina di accesso.
 *
 * **Un `Suspense` per rotta, non uno solo in cima.** React 18 tratta un cambio
 * di rotta come una *transition*, e durante una transition un boundary già
 * montato **non** mostra il proprio fallback: tiene a schermo il contenuto
 * precedente. Se il contenuto precedente è una guardia che ha appena rimbalzato
 * l'utente — cioè `null` — lo schermo resta bianco finché il chunk non arriva.
 * Un boundary che nasce insieme alla rotta invece il fallback lo mostra, perché
 * è nuovo.
 *
 * Dal 2026-10-05 (ADR 0030) la pagina iniziale è la Panoramica, le sezioni del
 * set sono voci della barra laterale (`/set/:id/:sezione`), l'amministrazione
 * sta sotto `/amministrazione` (dietro `RequireAdmin`, con le schede di
 * `AdminLayout`), le impostazioni del browser sotto `/impostazioni`, e il
 * catalogo dei ruoli è in sola lettura per tutti (`/ruoli`, D6). Dal 2026-10-05
 * (ADR 0032) ci sono anche `/task`, `/calendario`, `/aiuto` e
 * `/amministrazione/richieste`. Gli indirizzi di prima restano
 * come rimbalzi (ADR 0030 §2.4): `/abbonamento` e `/abbonamento/esito` non
 * cambiano, perché sono il ritorno da Stripe.
 */

import { Skeleton } from 'antd';
import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { useIsPlatformAdmin } from '../auth/roles';
import {
  LoginRoute,
  MfaEnrollRoute,
  MfaVerifyRoute,
  RecoveryCodesRoute,
  RequireSession,
  RequireStage,
} from './AuthRoutes';
import { RequireAdmin } from './RequireAdmin';
import {
  RELAY_LOG_PARAM,
  USAGE_ANCHOR,
  googleConnectionPath,
  homeFor,
  legacyPaths,
  legacySetTabs,
  paths,
  projectPath,
  projectTabs,
  setPath,
} from './routes';
import { useSetCapabilities } from './SetCapabilities';

const AppShell = lazy(async () => ({ default: (await import('./AppShell')).AppShell }));
const ForgotPasswordPage = lazy(async () => ({
  default: (await import('../pages/auth/ForgotPasswordPage')).ForgotPasswordPage,
}));
const NotFoundPage = lazy(async () => ({ default: (await import('./NotFoundPage')).NotFoundPage }));
const OverviewPage = lazy(async () => ({
  default: (await import('../pages/overview/OverviewPage')).OverviewPage,
}));
const HistoryPage = lazy(async () => ({
  default: (await import('../pages/history/HistoryPage')).HistoryPage,
}));
const SettingsPage = lazy(async () => ({
  default: (await import('../pages/settings/SettingsPage')).SettingsPage,
}));
const AdminLayout = lazy(async () => ({
  default: (await import('../pages/admin/AdminLayout')).AdminLayout,
}));
const SetsListPage = lazy(async () => ({
  default: (await import('../pages/sets/SetsListPage')).SetsListPage,
}));
const NewSetPage = lazy(async () => ({
  default: (await import('../pages/sets/wizard/NewSetPage')).NewSetPage,
}));
const SetPage = lazy(async () => ({ default: (await import('../pages/sets/SetPage')).SetPage }));
const ProjectsListPage = lazy(async () => ({
  default: (await import('../pages/projects/ProjectsListPage')).ProjectsListPage,
}));
const ProjectPage = lazy(async () => ({
  default: (await import('../pages/projects/ProjectPage')).ProjectPage,
}));
const ToolsPage = lazy(async () => ({ default: (await import('../pages/tools/ToolsPage')).ToolsPage }));
const PlatformPage = lazy(async () => ({
  default: (await import('../pages/tools/PlatformPage')).PlatformPage,
}));
const GoogleConnectionPage = lazy(async () => ({
  default: (await import('../pages/tools/google/manage/GoogleConnectionPage')).GoogleConnectionPage,
}));
const UsersPage = lazy(async () => ({
  default: (await import('../pages/users/UsersPage')).UsersPage,
}));
const TemplatesPage = lazy(async () => ({
  default: (await import('../pages/templates/TemplatesPage')).TemplatesPage,
}));
const BillingPage = lazy(async () => ({
  default: (await import('../pages/billing/BillingPage')).BillingPage,
}));
const ConsumptionPage = lazy(async () => ({
  default: (await import('../pages/consumption/ConsumptionPage')).ConsumptionPage,
}));
const CheckoutReturnPage = lazy(async () => ({
  default: (await import('../pages/billing/CheckoutReturnPage')).CheckoutReturnPage,
}));
const MonitoringPage = lazy(async () => ({
  default: (await import('../pages/monitoring/MonitoringPage')).MonitoringPage,
}));
const ProfilePage = lazy(async () => ({
  default: (await import('../pages/auth/profile/ProfilePage')).ProfilePage,
}));
const AllTasksPage = lazy(async () => ({
  default: (await import('../pages/tasks/AllTasksPage')).AllTasksPage,
}));
const CalendarPage = lazy(async () => ({
  default: (await import('../pages/calendar/CalendarPage')).CalendarPage,
}));
const HelpPage = lazy(async () => ({ default: (await import('../pages/help/HelpPage')).HelpPage }));
const GiftCodesPage = lazy(async () => ({
  default: (await import('../pages/admin/GiftCodesPage')).GiftCodesPage,
}));
const PriceListPage = lazy(async () => ({
  default: (await import('../pages/admin/PriceListPage')).PriceListPage,
}));
const DeadlinesPage = lazy(async () => ({
  default: (await import('../pages/admin/DeadlinesPage')).DeadlinesPage,
}));
const SupportRequestsPage = lazy(async () => ({
  default: (await import('../pages/help/SupportRequestsPage')).SupportRequestsPage,
}));

/**
 * Manda la radice alla pagina iniziale dell'utente.
 *
 * @returns Il rimbalzo, oppure il segnaposto finché le facoltà non arrivano.
 */
function HomeRedirect(): JSX.Element {
  const admin = useIsPlatformAdmin();
  const { data, loading } = useSetCapabilities();
  if (!admin && loading) {
    return <RouteFallback />;
  }
  return (
    <Navigate
      to={homeFor(admin, data?.owned_sets ?? null, data?.can_create_set ?? null)}
      replace
    />
  );
}

/**
 * Manda `/progetti/:projectId` alla sua prima scheda.
 *
 * @returns Il rimbalzo verso la panoramica del progetto.
 */
function ProjectIndexRedirect(): JSX.Element {
  const { projectId = '' } = useParams<{ projectId: string }>();
  return <Navigate to={projectPath(projectId, projectTabs.overview)} replace />;
}

/**
 * Manda l'elenco dei progetti di prima: all'amministrazione per l'`ADMIN`,
 * ai set per gli altri, che i progetti li aprono dal loro set.
 *
 * @returns Il rimbalzo.
 */
function ProjectsRedirect(): JSX.Element {
  const admin = useIsPlatformAdmin();
  return <Navigate to={admin ? paths.adminProjects : paths.sets} replace />;
}

/**
 * Manda al relay del set gli indirizzi senza sezione (`/set/:setId`), quelli
 * della conversazione di prima e quelli dei set di prima di ADR 0008
 * (`/progetti/:projectId/set/:setId`).
 *
 * @param props.log True per aprire il registro di chi sta lavorando (la scheda
 *   «Attività» di prima).
 * @returns Il rimbalzo verso il relay del set.
 */
function RelayRedirect({ log = false }: { log?: boolean }): JSX.Element {
  const { setId = '' } = useParams<{ setId: string }>();
  const target = setPath(setId);
  return <Navigate to={log ? `${target}?${RELAY_LOG_PARAM}=1` : target} replace />;
}

/**
 * Manda il percorso della pagina di Google scritto in ADR 0011
 * (`/tools/google/:connectionId`) a quello in italiano.
 *
 * @returns Il rimbalzo verso la pagina di gestione di Google.
 */
function GoogleConnectionRedirect(): JSX.Element {
  const { connectionId = '' } = useParams<{ connectionId: string }>();
  return <Navigate to={googleConnectionPath(connectionId)} replace />;
}

/**
 * Segnaposto mostrato mentre il codice di una pagina arriva.
 *
 * @returns L'elemento del segnaposto, con la forma del contenuto.
 */
function RouteFallback(): JSX.Element {
  return (
    <div aria-busy="true">
      <Skeleton active paragraph={{ rows: 6 }} title />
    </div>
  );
}

/**
 * Avvolge l'elemento di una rotta nel proprio confine di attesa.
 *
 * @param children Elemento della rotta.
 * @returns L'elemento con il suo `Suspense`.
 */
function lazyRoute(children: ReactNode): JSX.Element {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

/**
 * Tabella delle rotte dell'applicazione.
 *
 * @returns L'elemento con le rotte.
 */
export function AppRouter(): JSX.Element {
  return (
    <Routes>
      {/* Senza guardia: chi ha dimenticato la password non ha una sessione (ADR 0030 §6.4). */}
      <Route path={paths.forgotPassword} element={lazyRoute(<ForgotPasswordPage />)} />
      <Route element={<RequireStage stage={paths.login} />}>
        <Route path={paths.login} element={lazyRoute(<LoginRoute />)} />
      </Route>
      <Route element={<RequireStage stage={paths.mfaEnroll} />}>
        <Route path={paths.mfaEnroll} element={lazyRoute(<MfaEnrollRoute />)} />
      </Route>
      <Route element={<RequireStage stage={paths.mfaVerify} />}>
        <Route path={paths.mfaVerify} element={lazyRoute(<MfaVerifyRoute />)} />
      </Route>
      <Route element={<RequireStage stage={paths.recoveryCodes} />}>
        <Route path={paths.recoveryCodes} element={lazyRoute(<RecoveryCodesRoute />)} />
      </Route>

      <Route element={<RequireSession />}>
        <Route element={lazyRoute(<AppShell />)}>
          <Route path={paths.root} element={<HomeRedirect />} />
          <Route path={paths.overview} element={lazyRoute(<OverviewPage />)} />
          <Route path={paths.history} element={lazyRoute(<HistoryPage />)} />
          <Route path={paths.allTasks} element={lazyRoute(<AllTasksPage />)} />
          <Route path={paths.calendar} element={lazyRoute(<CalendarPage />)} />
          <Route path={paths.help} element={lazyRoute(<HelpPage />)} />
          <Route path={paths.profile} element={lazyRoute(<ProfilePage />)} />
          <Route path={paths.settings} element={lazyRoute(<SettingsPage />)} />

          <Route path={paths.sets} element={lazyRoute(<SetsListPage />)} />
          <Route path={paths.newSet} element={lazyRoute(<NewSetPage />)} />
          <Route path={`${paths.sets}/:setId`} element={<RelayRedirect />} />
          <Route path={`${paths.sets}/:setId/${legacySetTabs.conversation}`} element={<RelayRedirect />} />
          <Route path={`${paths.sets}/:setId/${legacySetTabs.activity}`} element={<RelayRedirect log />} />
          <Route path={`${paths.sets}/:setId/:tab`} element={lazyRoute(<SetPage />)} />
          {/* Pagina di un sito negli Artefatti: demo o produzione di un progetto. */}
          <Route path={`${paths.sets}/:setId/:tab/:projectId/:environment`} element={lazyRoute(<SetPage />)} />

          <Route element={<RequireAdmin />}>
            <Route path={paths.admin} element={<Navigate to={paths.adminUsers} replace />} />
            <Route element={lazyRoute(<AdminLayout />)}>
              <Route path={paths.adminUsers} element={lazyRoute(<UsersPage />)} />
              <Route path={paths.adminProjects} element={lazyRoute(<ProjectsListPage />)} />
              <Route path={paths.adminPlatform} element={lazyRoute(<PlatformPage />)} />
              <Route path={paths.adminMonitoring} element={lazyRoute(<MonitoringPage />)} />
              <Route path={paths.adminSupport} element={lazyRoute(<SupportRequestsPage />)} />
              <Route path={paths.adminGiftCodes} element={lazyRoute(<GiftCodesPage />)} />
              <Route path={paths.adminDeadlines} element={lazyRoute(<DeadlinesPage />)} />
              <Route path={paths.adminPriceList} element={lazyRoute(<PriceListPage />)} />
            </Route>
          </Route>

          <Route path={paths.projects} element={<ProjectsRedirect />} />
          <Route path={`${paths.projects}/:projectId`} element={<ProjectIndexRedirect />} />
          <Route path={`${paths.projects}/:projectId/set/:setId`} element={<RelayRedirect />} />
          <Route path={`${paths.projects}/:projectId/:tab`} element={lazyRoute(<ProjectPage />)} />
          <Route path={paths.tools} element={lazyRoute(<ToolsPage />)} />
          <Route path={`${paths.tools}/google/:connectionId`} element={lazyRoute(<GoogleConnectionPage />)} />
          <Route path="/tools/google/:connectionId" element={<GoogleConnectionRedirect />} />
          <Route path={paths.templates} element={lazyRoute(<TemplatesPage />)} />
          <Route path={paths.billing} element={lazyRoute(<BillingPage />)} />
          <Route path={paths.billingReturn} element={lazyRoute(<CheckoutReturnPage />)} />
          <Route path={paths.consumption} element={lazyRoute(<ConsumptionPage />)} />

          <Route
            path={legacyPaths.plan}
            element={<Navigate to={`${paths.billing}#${USAGE_ANCHOR}`} replace />}
          />
          <Route path={legacyPaths.users} element={<Navigate to={paths.adminUsers} replace />} />
          <Route path={legacyPaths.agentRoles} element={<Navigate to={paths.overview} replace />} />
          <Route path={legacyPaths.roles} element={<Navigate to={paths.overview} replace />} />
          <Route path={legacyPaths.monitoring} element={<Navigate to={paths.adminMonitoring} replace />} />

          <Route path="*" element={lazyRoute(<NotFoundPage />)} />
        </Route>
      </Route>
    </Routes>
  );
}
